import debounce from "lodash/debounce";
import find from "lodash/find";
import isNull from "lodash/isNull";
import isEmpty from "lodash/isEmpty";
import { useIonRouter } from "@ionic/react";

import useWLPromoStore from "../../../components/WLPromoField/store";
import useUserSubscriptionsStore from "../../../CommonStores/subscriptions/store";
import { useEffect, useState } from "react";
import Stripe from "stripe";
import { usePurchaseV2Store } from "./store";
import { useWebUserStore } from "../../../CommonStores/user/users";
import { KlaviyoPurchaseTypes } from "../../../../Common/config/global";
import useSuperGiveAwayStore from "../../../CommonStores/giveaways/store";
import { getErrorMsg } from "../../../../Common/utils/common";
import { bugNotif } from "../../../api/bugsnag";
import { User } from "../../../../Common/models/user";
import {
  ViewWLSubscriptionsTiers,
  WLStripeWPlanSubscription,
} from "../../../../Common/models/subscriptions";
import { getStripePriceValueWithPromo } from "../../../services/promo";
import { pushKlaviyoPurchaseEvent } from "../../../api/klaviyo";
import useWLConfigStore from "../../../CommonStores/wlConfig/store";

export type PurchaseType =
  | "powerup"
  | "subscription"
  | "upgrade"
  | "downgrade"
  | "powerupWithPoints"
  | "retryPayment";

const readSearchParam = (key: string): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  const value = new URLSearchParams(window.location.search).get(key);
  if (!isNull(value) && !isEmpty(value)) {
    return value;
  }
  return undefined;
};

const hasDrawTonightSearchParam = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return new URLSearchParams(window.location.search).has("drawTonight");
};

const useWebPaymentV2Provider = () => {
  const router = useIonRouter();

  const [wlConfig] = useWLConfigStore((state) => [state.wlConfig]);

  const { user } = useWebUserStore();

  const {
    promoValidating,
    validStripeCoupon,
    validPromoCode,
    validCouponCode,
    defaultPromoCode,
    setDefaultPromoCode,
  } = useWLPromoStore();

  const [mode, setMode] = useState<undefined | PurchaseType>(() => {
    const fromUrl = readSearchParam("mode");
    return fromUrl ? (fromUrl as PurchaseType) : undefined;
  });
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>(() =>
    readSearchParam("subscriptionId"),
  );
  const [isDrawTonight, setIsDrawTonight] = useState<boolean>(() =>
    hasDrawTonightSearchParam(),
  );

  const [recorededCart, setRecordedCart] = useState("");

  const {
    setSelectedProduct,
    selectedProduct,
    setShowStripePaymentMethod,
    doubleForHalfOffAvailed,
    setOneTimeOffer,
    reset: resetStore,
  } = usePurchaseV2Store();

  const reset = () => {
    setDefaultPromoCode(undefined);
    setMode(undefined);
    setIsDrawTonight(false);
    resetStore();
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const promoCode = searchParams.get("promo");
    if (!isNull(promoCode) && !isEmpty(promoCode)) {
      const promo = promoCode.toUpperCase().trim();
      setDefaultPromoCode(promo);
    } else {
      setDefaultPromoCode(null);
    }

    const mode = searchParams.get("mode");
    if (!isNull(mode) && !isEmpty(mode)) {
      setMode(mode as PurchaseType);
    }

    const subscriptionId = searchParams.get("subscriptionId");
    if (!isNull(subscriptionId) && !isEmpty(subscriptionId)) {
      setSubscriptionId(subscriptionId);
    }

    const oneTimeOffer = searchParams.get("oneTimeOffer");
    if (!isNull(oneTimeOffer) && !isEmpty(oneTimeOffer)) {
      setOneTimeOffer(!!oneTimeOffer);
    }

    setIsDrawTonight(searchParams.has("drawTonight"));

    if (!mode || !subscriptionId) {
      // router.push(DASHBOARD, "back", "pop");
    }

    return () => {
      reset();
    };
  }, []);

  const { powerUpList, subscriptionList } = useUserSubscriptionsStore();
  const [stripeActiveSubscription] = useWebUserStore((store) => [
    store.stripeActiveSubscription,
  ]);
  useEffect(() => {
    const showStripePaymentMethod =
      !!user &&
      !!user.stripeId &&
      !!selectedProduct &&
      mode !== "powerupWithPoints";

    setShowStripePaymentMethod(showStripePaymentMethod);

    if (stripeActiveSubscription) {
      const stripePlan = stripeActiveSubscription.plan;
      const stripeProductId = stripePlan?.product as string;
      if (stripePlan !== undefined && stripeProductId !== null) {
        setSubscriptionId(stripeProductId);
      }
    }
  }, [user, mode, selectedProduct, stripeActiveSubscription]);

  useEffect(() => {
    if (
      subscriptionId !== undefined &&
      !isNull(powerUpList) &&
      !isNull(subscriptionList)
    ) {
      const selectedProduct =
        find(
          [...powerUpList, ...subscriptionList],
          (product) => product.docId === subscriptionId,
        ) || null;

      if (isNull(selectedProduct)) {
        // router.push(DASHBOARD, "back", "pop");
      } else {
        setSelectedProduct(selectedProduct);
      }
    }
  }, [powerUpList, subscriptionList, subscriptionId]);

  const { ongoingGiveaway } = useSuperGiveAwayStore();

  useEffect(() => {
    if (user === undefined || isNull(user)) {
      return;
    }

    if (selectedProduct === undefined) {
      return;
    }

    if (isNull(ongoingGiveaway)) {
      return;
    }

    if (mode === undefined) {
      return;
    }

    if (promoValidating) {
      return;
    }

    pushKlaviyoAddtoCart(
      mode,
      selectedProduct,
      validStripeCoupon,
      validPromoCode || validCouponCode,
      ongoingGiveaway.code || "N/A",
      doubleForHalfOffAvailed,
      user,
    );
  }, [
    mode,
    selectedProduct,
    validStripeCoupon,
    validPromoCode,
    validCouponCode,
    ongoingGiveaway,
    user,
    promoValidating,
    doubleForHalfOffAvailed,
  ]);

  const pushKlaviyoAddtoCart = debounce(
    async (
      orderType: KlaviyoPurchaseTypes,
      selectedProduct: ViewWLSubscriptionsTiers,
      validStripeCoupon: Stripe.Coupon | null | undefined,
      validPromoCode: string | undefined,
      giveawayCode: string,
      doubleForHalfOffAvailed,
      user: User,
    ) => {
      try {
        const { docId, price, name } = selectedProduct;
        let value =
          validStripeCoupon !== undefined && !isNull(validStripeCoupon)
            ? getStripePriceValueWithPromo(price, validStripeCoupon)
            : price;

        if (orderType === "powerup") {
          const doubleForHalfOffAvailedCost = doubleForHalfOffAvailed
            ? price / 2
            : 0;

          value += doubleForHalfOffAvailedCost;
        }
        const newRecordedCart = `${orderType}-${docId}-${value}-${name}-${
          validPromoCode || ""
        }-${doubleForHalfOffAvailed ? "double" : "not-double"}`;

        if (recorededCart === newRecordedCart) {
          return;
        }
        setRecordedCart(newRecordedCart);
        await pushKlaviyoPurchaseEvent({
          orderEvent: "StartCheckout",
          orderType,
          productId: docId,
          value,
          productName: name,
          promoCode: validPromoCode || "",
          giveawayCode: giveawayCode || "",
          emailAddress: user.emailAddress,
          doubleForHalfOffAvailed,
        });
      } catch (error) {
        const { errorMsg, rawErrorMsg } = getErrorMsg(error);
        setRecordedCart("");
        console.log("error sending metric", errorMsg);
        bugNotif("pushKlaviyoAddtoCart", rawErrorMsg);
      }
    },
    1000,
  );

  return {
    mode,
    subscriptionId,
    selectedProduct,
    isDrawTonightMode: isDrawTonight,
  };
};

export default useWebPaymentV2Provider;
